import { DataType, DateTime, ExprCall, ExprForm } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import {
  BasicChannel,
  EncChannel,
  EncField,
  EncFieldGroup,
  EncFilterField,
  Encoding,
  ExprFrame,
} from '@mode-vegas/vegas';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledAttr, CompiledField, CompiledFormula } from '@mode/shared/util-switch';
import { format, startOfWeek } from 'date-fns';
import { partition } from 'lodash';
import { DndDropEvent } from 'ngx-drag-drop';

type ModeView = FlamingoTypes.ModeView;

export interface OnDropEvent {
  event: DndDropEvent;
  channel: BasicChannel;
  onFinalize?: Function;
  attr?: CompiledAttr;
}

export interface FieldDropData {
  event: DndDropEvent;
  field: EncField | EncFilterField;
  channel: BasicChannel;
  group?: EncFieldGroup;
}

export interface DragStartData {
  event: DndDropEvent;
  field: EncField | EncFieldGroup;
  source: EncChannel;
}

export interface DragEndData {
  event: DragEvent;
  field: EncField;
}

interface FrameInfo {
  frameAddress: string;
  frameType: string;
}

export const DateTruncOpts = [
  { unit: DateTime.Unit.Year, label: 'Year', format: 'yyyy', isMore: false },
  { unit: DateTime.Unit.Quarter, label: 'Quarter', format: 'QQQ yyyy', isMore: false },
  { unit: DateTime.Unit.Month, label: 'Month', format: 'MMM yyyy', isMore: false },
  { unit: DateTime.Unit.Week, label: 'Week', format: 'MMM d yyyy', isMore: false },
  { unit: DateTime.Unit.Day, label: 'Day', format: 'MMM d yyyy', isMore: false },
  { unit: DateTime.Unit.Hour, label: 'Hour', format: 'haaa MMM d yyyy', isMore: true },
  { unit: DateTime.Unit.Minute, label: 'Minute', format: 'h:mmaaa MMM d yyyy', isMore: true },
  { unit: DateTime.Unit.Second, label: 'Second', format: 'h:mm:ssaaa MMM d yyyy', isMore: true },
];

export const DatePartOpts = [
  { unit: DateTime.Unit.Year, label: 'Year', format: 'yyyy', isMore: false },
  { unit: DateTime.Unit.Quarter, label: 'Quarter', format: 'QQQ', isMore: false },
  { unit: DateTime.Unit.Month, label: 'Month', format: 'MMM', isMore: false },
  { unit: DateTime.Unit.Weekday, label: 'Weekday', format: 'ccc', isMore: false },
  { unit: DateTime.Unit.Day, label: 'Day', format: 'd', isMore: false },
  { unit: DateTime.Unit.Hour, label: 'Hour', format: 'haaa', isMore: true },
  { unit: DateTime.Unit.Minute, label: 'Minute', format: 'm', isMore: true },
  { unit: DateTime.Unit.Second, label: 'Second', format: 's', isMore: true },
];

export const AggregateOpts = [
  { aggregate: ExprCall.Name.Sum, label: 'Sum', summable: true, summableOrCountable: false },
  { aggregate: ExprCall.Name.Count, label: 'Count', summable: true, summableOrCountable: true },
  { aggregate: ExprCall.Name.CountD, label: 'Count (distinct)', summable: true, summableOrCountable: true },
  { aggregate: ExprCall.Name.CountDA, label: 'Approx. count (distinct)', summable: true, summableOrCountable: true },
  { aggregate: ExprCall.Name.Avg, label: 'Average', summable: true, summableOrCountable: false },
  { aggregate: ExprCall.Name.Max, label: 'Maximum', summable: true, summableOrCountable: false },
  { aggregate: ExprCall.Name.Min, label: 'Minimum', summable: true, summableOrCountable: false },
];

export const WeekStartOptions = [
  { value: DateTime.WeekStart.Sunday, name: 'Sunday' },
  { value: DateTime.WeekStart.Monday, name: 'Monday' },
  { value: DateTime.WeekStart.Tuesday, name: 'Tuesday' },
  { value: DateTime.WeekStart.Wednesday, name: 'Wednesday' },
  { value: DateTime.WeekStart.Thursday, name: 'Thursday' },
  { value: DateTime.WeekStart.Friday, name: 'Friday' },
  { value: DateTime.WeekStart.Saturday, name: 'Saturday' },
];

export const YearStartOptions = [
  { value: DateTime.YearStart.January, name: 'January' },
  { value: DateTime.YearStart.February, name: 'February' },
  { value: DateTime.YearStart.March, name: 'March' },
  { value: DateTime.YearStart.April, name: 'April' },
  { value: DateTime.YearStart.May, name: 'May' },
  { value: DateTime.YearStart.June, name: 'June' },
  { value: DateTime.YearStart.July, name: 'July' },
  { value: DateTime.YearStart.August, name: 'August' },
  { value: DateTime.YearStart.September, name: 'September' },
  { value: DateTime.YearStart.October, name: 'October' },
  { value: DateTime.YearStart.November, name: 'November' },
  { value: DateTime.YearStart.December, name: 'December' },
];

export interface DateUnitOpt {
  unit: DateTime.Unit;
  isSelected: boolean;
  label: string;
  formatted: string;
}

export function buildDateTruncMenuOpts(compiled: CompiledField) {
  const NOW = new Date();
  const isDateTrunc = compiled.form === ExprForm.DateTrunc;

  const mapFunc = (item: { unit: DateTime.Unit; label: string; format: string }): DateUnitOpt => {
    return {
      unit: item.unit,
      isSelected: isDateTrunc && compiled.dateFuncUnit === item.unit,
      label: item.label,
      formatted: item.unit === DateTime.Unit.Week ? format(startOfWeek(NOW), item.format) : format(NOW, item.format),
    };
  };

  const mainMenuOpts = DateTruncOpts.filter((opt) => !opt.isMore).map((item) => mapFunc(item));
  const moreMenuOpts = DateTruncOpts.filter((opt) => opt.isMore).map((item) => mapFunc(item));
  return { mainMenuOpts, moreMenuOpts };
}

export function buildDatePartMenuOpts(compiled: CompiledField) {
  const NOW = new Date();
  const isDatePart = compiled.form === ExprForm.DatePart;

  const mapFunc = (item: { unit: DateTime.Unit; label: string; format: string }): DateUnitOpt => {
    return {
      unit: item.unit,
      isSelected: isDatePart && compiled.dateFuncUnit === item.unit,
      label: item.label,
      formatted: item.unit === DateTime.Unit.Weekday ? format(startOfWeek(NOW), item.format) : format(NOW, item.format),
    };
  };

  const mainMenuOpts = DatePartOpts.filter((opt) => !opt.isMore).map((item) => mapFunc(item));
  const moreMenuOpts = DatePartOpts.filter((opt) => opt.isMore).map((item) => mapFunc(item));
  return { mainMenuOpts, moreMenuOpts };
}

export function buildAnalyticCalculationDescription(
  field: EncField,
  compiledField: CompiledFormula | null
): string | null {
  if (!compiledField || (!compiledField.hasAnalyticFunc && !compiledField.isAnalyticCalc)) {
    return null;
  }
  const exprForm = getDescriptionExprForm(compiledField);
  const { frameAddress, frameType } = getDescriptionFrameInfo(field);
  const relativeTo = getDescriptionRelativeTo(compiledField.relativeTo);
  const descriptionSubject = exprForm
    ? `Calculate ${compiledField.displayFormula} as`
    : frameType
      ? `${compiledField.displayFormula} - Results are`
      : compiledField.displayFormula;
  return `${descriptionSubject}${exprForm}${frameAddress}${frameType}${relativeTo}`;
}

export function concatDescriptionFrameAndRelativeTo(
  field: EncField,
  compiledField: CompiledFormula | undefined
): string {
  if (!compiledField?.hasAnalyticFunc && !compiledField?.isAnalyticCalc) {
    return '';
  }
  const { frameAddress, frameType } = getDescriptionFrameInfo(field);
  const frameRelativeTo = getDescriptionRelativeTo(compiledField.relativeTo);
  return `${frameAddress}${frameType}${frameRelativeTo}`;
}

export function getDescriptionExprForm(compiledField: CompiledFormula | null, toLowerCase = true): string {
  const capitalizeQuickCalcExprFormMap = {
    [ExprForm.PctOfTotal]: '% Total',
    [ExprForm.PctDifference]: '% Difference',
    [ExprForm.Difference]: 'Difference',
  };

  switch (compiledField?.form) {
    case ExprForm.PctDifference: {
      if (toLowerCase) {
        return ` percent of difference`;
      } else {
        return ` ${capitalizeQuickCalcExprFormMap[compiledField.form]}`;
      }
    }
    case ExprForm.PctOfTotal: {
      if (toLowerCase) {
        return ` percent of total`;
      } else {
        return ` ${capitalizeQuickCalcExprFormMap[compiledField.form]}`;
      }
    }
    case ExprForm.Difference: {
      if (toLowerCase) {
        return ` ${compiledField?.form?.toLowerCase()}`;
      } else {
        return ` ${capitalizeQuickCalcExprFormMap[compiledField.form]}`;
      }
    }
    default: {
      return '';
    }
  }
}

export function getDescriptionFrameInfo(field: EncField): FrameInfo {
  if (field.frame == null) {
    return {
      frameAddress: '',
      frameType: '',
    };
  }

  let frameAddress = '';
  switch (field.frame.type) {
    case ExprFrame.Type.Table:
    case ExprFrame.Type.Pane: {
      frameAddress = field.frame.address;
      break;
    }
  }

  return {
    frameAddress: ` ${frameAddress.replace('_', ' ').toLowerCase()}`.trimEnd(),
    frameType: ` ${field.frame.type.toLowerCase()}`.trimEnd(),
  };
}

export function getDescriptionRelativeTo(relativeTo: ExprForm.RelativeTo | undefined): string {
  switch (relativeTo) {
    case ExprForm.RelativeTo.Prev: {
      return ` relative to previous value`;
    }
    case ExprForm.RelativeTo.First: {
      return ` relative to first value`;
    }
    case ExprForm.RelativeTo.Next:
    case ExprForm.RelativeTo.Last:
      return ` relative to ${relativeTo.toLowerCase()} value`;
    default: {
      return ``;
    }
  }
}

export interface IOpt<T> {
  readonly value: T;
  readonly name: string;
  readonly desc?: string;
  readonly icon?: string;
  readonly divider?: boolean;
}

export type CompiledAttrOpt = IOpt<CompiledAttr>;
export type AggFnOpt = IOpt<ExprCall.Name>;

export function isValidFormula(compiled: CompiledFormula): boolean {
  return !!(compiled.isValid && compiled.attrName && !compiled.isAnalyticCalc);
}

export function makePartitionedByFieldsLists(
  view: FlamingoTypes.ModeView,
  encoding: Encoding
): [CompiledAttrOpt[], CompiledAttrOpt[]] {
  const encodingFields = getCompiledFieldsUsedInEncoding(view, encoding);
  const allFieldsList = makeByFieldsList(view);
  return partition(allFieldsList, (item) =>
    encodingFields.some((fieldInfo) => fieldInfo.attrName === item.value.attrName)
  );
}

export function makeByFieldsList(view: FlamingoTypes.ModeView): CompiledAttrOpt[] {
  return view.attrs
    .map((attr) => compileAttr(view, attr))
    .filter(isValidFormula)
    .sort(attrSortFn)
    .map((attr) => {
      const icon = iconForAttr(attr);
      return {
        name: attr.attrName ?? '<SHOULD NOT HAPPEN>',
        value: attr,
        icon,
      };
    });
}

function getCompiledFieldsUsedInEncoding(view: FlamingoTypes.ModeView, encoding: Encoding): Array<CompiledField> {
  if (!(view && encoding)) {
    return [];
  }

  const fields = EncodingHelpers.collectEncFields(encoding);
  const fieldsInUse = new Map<string, CompiledField>();

  for (const field of fields) {
    const compiled = CompiledField.fromEncField(view, field);
    if (compiled.attrName && !fieldsInUse.has(compiled.attrName)) {
      fieldsInUse.set(compiled.attrName, compiled);
    }
  }

  return Array.from(fieldsInUse.values());
}

function compileAttr(view: FlamingoTypes.ModeView, attr: FlamingoTypes.ModeAttr): CompiledAttr {
  return CompiledAttr.fromAttr(view, attr);
}

export function compileField(view: ModeView, field: EncField): CompiledField {
  return CompiledField.fromEncField(view, field);
}

function attrSortFn(attr1: CompiledAttr, attr2: CompiledAttr): number {
  return +attr1.isDiscrete - +attr2.isDiscrete || String(attr1.attrName).localeCompare(String(attr2.attrName));
}

export function iconForAttr(attr: CompiledAttr): string {
  const prefix = attr.source === Attr.Source.Calc ? 'icon-field-calc' : 'icon-field';
  switch (attr.formulaDataType) {
    case DataType.Name.Boolean:
      return `${prefix}-boolean`;
    case DataType.Name.DateTime:
    case DataType.Name.Timestamp:
      return `${prefix}-date`;
    case DataType.Name.Integer:
    case DataType.Name.Float:
    case DataType.Name.Decimal:
      return `${prefix}-numeric`;
    default: {
      return `${prefix}-string`;
    }
  }
}

function aggFnOptsForFormula(compiled: CompiledFormula): readonly AggFnOpt[] {
  if (compiled.isNonScalarCalc) {
    return customAggOptions;
  }

  switch (compiled.formulaDataType) {
    case DataType.Name.Integer:
    case DataType.Name.Float:
    case DataType.Name.Decimal: {
      return summableAggOptions;
    }
    default: {
      return countableAggOptions;
    }
  }
}

export function aggFnsForFormula(compiled: CompiledFormula, includeNoAggr = false): readonly AggFnOpt[] {
  const aggFuncs = aggFnOptsForFormula(compiled);
  return includeNoAggr ? noAggOptions.concat(aggFuncs) : aggFuncs;
}

export function attrName(compiled: CompiledFormula): string | undefined {
  if (compiled.attrName != null) {
    return compiled.attrName;
  }
}

export function callName(compiled: CompiledFormula | undefined, fallbackToDefault = true): ExprCall.Name | undefined {
  if (compiled?.attrName != null) {
    let name = <ExprCall.Name>compiled.aggFunc;
    if (name == null && fallbackToDefault) {
      const fns = aggFnsForFormula(compiled);
      name = fns[0].value;
    }
    return ExprCall.Name.make(name ?? '');
  }
}

export const noAggOptions: ReadonlyArray<AggFnOpt> = [
  {
    name: 'No Aggregation',
    value: ExprCall.Name.Any,
  },
];

export const customAggOptions: ReadonlyArray<AggFnOpt> = [
  {
    name: 'Custom',
    value: ExprCall.Name.Any,
  },
];

export const countableAggOptions: ReadonlyArray<AggFnOpt> = [
  {
    name: 'Count',
    value: ExprCall.Name.Count,
  },
  {
    name: 'Count (Distinct)',
    value: ExprCall.Name.CountD,
  },
  {
    name: 'Approx. Count (Distinct)',
    value: ExprCall.Name.CountDA,
  },
  {
    name: 'Minimum',
    value: ExprCall.Name.Min,
  },
  {
    name: 'Maximum',
    value: ExprCall.Name.Max,
  },
];

export const summableAggOptions: ReadonlyArray<AggFnOpt> = [
  {
    name: 'Sum',
    value: ExprCall.Name.Sum,
  },
  {
    name: 'Average',
    value: ExprCall.Name.Avg,
  },
  {
    name: 'Median',
    value: ExprCall.Name.Median,
  },
  {
    name: 'Count',
    value: ExprCall.Name.Count,
  },
  {
    name: 'Count (Distinct)',
    value: ExprCall.Name.CountD,
  },
  {
    name: 'Approx. Count (Distinct)',
    value: ExprCall.Name.CountDA,
  },
  {
    name: 'Minimum',
    value: ExprCall.Name.Min,
  },
  {
    name: 'Maximum',
    value: ExprCall.Name.Max,
  },
];

export function find<T extends IOpt<unknown>>(
  arr: readonly T[],
  predicate: (value: T['value']) => boolean
): T | undefined {
  return arr.find((entry) => predicate(entry.value));
}

export function findOrFirst<T extends IOpt<unknown>>(arr: readonly T[], predicate: (value: T['value']) => boolean): T {
  return find(arr, predicate) ?? arr[0];
}
